import { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Send, X, Globe } from "lucide-react";
import ChatName from "@/components/ChatName";

export default function GlobalChat() {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  const refresh = async () => {
    try { setMsgs(await api.chatGlobal()); } catch (e) { /* silent */ }
  };
  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 5000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (open) endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [open, msgs.length]);

  const send = async () => {
    if (!msg.trim() || busy) return;
    setBusy(true);
    try {
      const res = await api.chatGlobalSend(msg.trim());
      if (res && res.ok === false && res.discipline) {
        const d = res.discipline;
        if (d.muted) {
          toast.error(d.reason || "You are muted. Open a ticket to appeal.", { duration: 4000 });
        } else {
          toast(d.reason || `Warning ${d.warning_count}/3`, { duration: 4000 });
        }
        setMsg("");
      } else {
        setMsg("");
      }
      await refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Chat failed"); }
    finally { setBusy(false); }
  };

  return (
    <>
      {/* Floating toggle button */}
      <button
        data-testid="global-chat-toggle"
        onClick={() => setOpen(!open)}
        className="fixed bottom-24 right-3 z-40 w-11 h-11 border-2 border-[#B89C3A] bg-[#050505] text-[#E5C34A] flex items-center justify-center hover:border-[#E55A00] hover:text-[#E55A00] transition-colors"
        aria-label="Global chat"
      >
        {open ? <X className="w-4 h-4" strokeWidth={1.5} /> : <Globe className="w-4 h-4 ember-pulse" strokeWidth={1.5} />}
      </button>

      {open && (
        <div
          data-testid="global-chat-panel"
          className="fixed bottom-24 right-16 left-3 sm:left-auto sm:w-80 z-40 panel bg-[#050505]"
          style={{ maxHeight: "60vh" }}
        >
          <div className="px-3 py-2 border-b border-[#262626] flex items-center gap-2">
            <Globe className="w-3 h-3 text-[#E55A00]" strokeWidth={1.5} />
            <span className="font-heading text-xs tracking-[0.3em] uppercase text-[#8c8c8c]">
              World Chat
            </span>
            <span className="ml-auto font-mono text-[10px] text-[#666]">
              {msgs.length} msgs
            </span>
          </div>
          <div className="overflow-y-auto p-3 space-y-1" style={{ maxHeight: "40vh" }}>
            {msgs.length === 0 && (
              <p className="italic text-[#666] text-xs">Silence hangs over the fog.</p>
            )}
            {msgs.map((c, i) => (
              <div key={i} className="font-body text-xs" data-testid={c.is_admin ? "chat-msg-admin" : undefined}>
                {c.kind === "emote" ? (
                  <>
                    <ChatName name={c.name} isAdmin={c.is_admin} isPreAlpha={c.is_pre_alpha} />{" "}
                    <span className="italic text-[#E5C34A]">{c.text}</span>
                  </>
                ) : (
                  <>
                    <ChatName name={c.name} isAdmin={c.is_admin} isPreAlpha={c.is_pre_alpha} colon />{" "}
                    <span className="text-[#B0ADA5]">{c.text}</span>
                  </>
                )}
              </div>
            ))}
            <div ref={endRef} />
          </div>
          <div className="flex border-t border-[#262626]">
            <input
              data-testid="global-chat-input"
              value={msg}
              onChange={(e) => setMsg(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Speak to the world... (try /bow /toast /laugh)"
              maxLength={200}
              className="flex-1 bg-transparent px-3 py-2 text-sm text-[#E2DFD8] outline-none placeholder-[#555]"
            />
            <button
              data-testid="global-chat-send"
              onClick={send}
              disabled={busy || !msg.trim()}
              className="px-3 border-l border-[#262626] text-[#E55A00] hover:bg-[#1a1a1a] disabled:opacity-30"
            >
              <Send className="w-4 h-4" strokeWidth={1.5} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
